// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import ControlTemplate from '@front/templates/ControlTemplate.html';
import PositionAndVisibleTemplate from '@front/templates/PositionAndVisibleTemplate.html';
import VisibleTemplate from '@front/templates/VisibleTemplate.html';
import VisualRulesTemplate from '@front/templates/VisualRulesTemplate.html';
import { buttonsPopupHandler, datePickerDropDownHandler, multiComboBoxPopupMenuHandler } from '@shared/utils/AppHandlers';
import Ractive from 'ractive';
import { ReactToRactiveAdapter } from '../ReactToRactiveAdapter';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class Control extends ReactToRactiveAdapter {
    constructor() {
        super(...arguments);
        this.AfterLostFocusWaiting = null;
    }
    getType() {
        return 'Control';
    }
    oncomplete() {
        this.observe('focused', (newVal, oldValue) => {
            if (!newVal && oldValue) {
                this.fire(ControlEvents.LostFocus);
            }
        });
    }
    setLocation(x, y) {
        void this.set({ left: x, top: y });
    }
    setBounds(x, y, width, height) {
        void this.set({
            left: x,
            top: y,
            width,
            height
        });
    }
    getBounds() {
        const all = this.get();
        return {
            left: all.left,
            top: all.top,
            width: all.width,
            height: all.height
        };
    }
    lostFocus() {
        void this.set('focused', false);
    }
    gotFocus() {
        void this.set('focused', true);
        // if (this.el)
        //    this.el.focus();
        this.fire(ControlEvents.GotFocus);
    }
    setFocus(firstParam, secondParam) {
        const parentControl = this.get('parentContainerControl');
        if (parentControl) {
            parentControl.processFocus(this);
        }
    }
    onMouseDown(event, isHeader = false) {
        var _a;
        if (Control.IsEventProcessed(event)) {
            return;
        }
        // Inform parent control about changes
        const parentControl = (_a = this.get('parentContainerControl')) !== null && _a !== void 0 ? _a : this.parent;
        if (parentControl !== null) {
            parentControl.processFocus(this);
        }
        if (!isNullOrUndefined(multiComboBoxPopupMenuHandler === null || multiComboBoxPopupMenuHandler === void 0 ? void 0 : multiComboBoxPopupMenuHandler.isShowed) && multiComboBoxPopupMenuHandler.isShowed()) {
            multiComboBoxPopupMenuHandler.ProcessOnMouseDown(this);
        }
        if (!isNullOrUndefined(datePickerDropDownHandler.get) && datePickerDropDownHandler.get('visible')) {
            datePickerDropDownHandler.Hide();
        }
        if (!isNullOrUndefined(buttonsPopupHandler) &&
            !isNullOrUndefined(buttonsPopupHandler.get) &&
            buttonsPopupHandler.get('visible') === true) {
            buttonsPopupHandler.Hide();
        }
        // Event was processed - skip next time
        Control.ProcessEvent(event);
    }
    onMouseMove(event) { }
    onMouseUp(event) { }
    onMouseWheel(event, secondParam) { }
    onDoubleClick(event) { }
    onClick(event) { }
    onMouseEnter(event) { }
    onMouseLeave(event) { }
    onContextMenuOpen(event) {
        if (Control.IsEventProcessed(event)) {
            return;
        }
        const myContextMenu = this.get('contextMenu');
        const ev = event.original;
        if (myContextMenu) {
            myContextMenu.show(this, ev.pageX, ev.pageY);
        }
        Control.ProcessEvent(event);
    }
    getContextMenuItem() {
        return {
            type: NewContextMenuItemType.Default,
            text: this.get('contextMenuItemText')
        };
    }
    getX() {
        let result = this.get('left');
        const parentContainerControl = this.get('parentContainerControl');
        if (parentContainerControl) {
            result += parentContainerControl.get('left');
        }
        const tmpleft = this.el.getBoundingClientRect().left;
        return result > tmpleft ? result : tmpleft;
    }
    getY() {
        let result = this.get('top');
        const parentContainerControl = this.get('parentContainerControl');
        if (parentContainerControl) {
            result += parentContainerControl.get('top');
        }
        const tmptop = this.el.getBoundingClientRect().top;
        return result > tmptop ? result : tmptop;
    }
    dispose() {
        void this.teardown();
    }
    getAbsoluteRectangle() {
        throw new Error('Not implemented');
    }
    static ProcessEvent(event) {
        const processedEvents = Control.processedEvents;
        const ev = event.original;
        // remember event
        processedEvents.push(ev.type + ev.timeStamp);
        // control size
        if (processedEvents.length > 50) {
            processedEvents.splice(0, processedEvents.length - 10);
        }
    }
    static IsEventProcessed(event) {
        const ev = event.original;
        return Control.processedEvents.includes(ev.type + ev.timeStamp);
    }
}
Control.Ticker = new CustomEvent();
// Processing events
// События прокидываются от контрола наверх, чтобы избежать повторной обработки - устанавливаем флаг.
// Пока оставляю механизм через статику, по возможности придумать как просто через event передавать данные.
// AlexB
Control.processedEvents = [];
export var ControlEvents;
(function (ControlEvents) {
    ControlEvents["LostFocus"] = "LostFocus";
    ControlEvents["GotFocus"] = "GotFocus";
})(ControlEvents || (ControlEvents = {}));
Ractive.extendWith(Control, {
    template: ControlTemplate,
    data: function () {
        return {
            // у всех наследников
            additionalClass: '',
            width: '',
            height: '',
            top: '',
            bottom: '',
            left: '',
            right: '',
            borderWidth: '', //
            visible: true, // видимость элемента элемента
            enabled: true, // возможность выделения элемента
            name: '', // имя элемента внутри контрола родителя - задавать обязательно! по нему рактив контрол попадет в Controls.
            tooltip: '', // всплывающая подсказка
            contextMenuItemText: '', // text for getContextMenuItem
            canSelect: true, // может ли контрол принимать фокус
            canFocus: true,
            showDisabledToolTip: true,
            parentContainerControl: null, // ссылка на родительский контейнер
            focused: false, // состояние фокуса
            onProcessMove: false,
            movable: false,
            resizable: false,
            withOutBorder: false,
            readonly: false,
            PRIVATE: {}
        };
    },
    partials: {
        position: PositionAndVisibleTemplate,
        visibility: VisibleTemplate,
        visualparameters: VisualRulesTemplate
    }
});
